import type { AssignedProduct } from '@discovr/types';

/**
 * Product-match verdict for content review (Multi-Product Campaigns):
 *   - `match`    — the AI-detected product is one of the creator's assigned ones
 *   - `mismatch` — a product was detected but it isn't assigned
 *   - `none`     — the feature is dormant; render nothing
 */
export type ProductMatchVerdict =
  | { kind: 'match'; product: string }
  | { kind: 'mismatch'; detected: string }
  | { kind: 'none' };

/**
 * Sentinels the AI returns when it couldn't identify a promoted product. The
 * video_review prompt instructs the model to emit 'none' rather than guess, so we
 * must NOT treat these as a real detection (that would render a false mismatch).
 */
const NO_PRODUCT_DETECTED = new Set(['', 'none', 'n/a', 'na', 'unknown', 'unclear']);

/**
 * computeProductMatch — pure FE verdict. Compares the AI-detected promoted
 * product against the creator's assigned products (case-insensitive, trimmed).
 *
 * Returns `none` (the caller renders nothing) whenever the feature is dormant or
 * there's nothing to judge:
 *   - the campaign has < 2 products (global gate — 0-1 campaigns unchanged),
 *   - the creator has no assignment, or
 *   - the AI produced no detected product (empty or a 'none'/'unclear' sentinel).
 * Never fabricates a verdict from missing or inconclusive data.
 */
export function computeProductMatch(
  assigned: AssignedProduct[] | undefined,
  detected: string | undefined,
  campaignProductCount?: number,
): ProductMatchVerdict {
  if (campaignProductCount !== undefined && campaignProductCount < 2) return { kind: 'none' };
  const det = (detected ?? '').trim();
  if (!assigned || assigned.length === 0 || NO_PRODUCT_DETECTED.has(det.toLowerCase())) {
    return { kind: 'none' };
  }

  const hit = assigned.find((a) => a.name.trim().toLowerCase() === det.toLowerCase());
  return hit ? { kind: 'match', product: hit.name } : { kind: 'mismatch', detected: det };
}
